import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AskRynUiService } from '@ups/xplat/web/ask-ryn';
import { HelpActions } from '@ups/xplat/base/in-app-help';

import { WREN_FLIGHT_SPRITE } from './walkthrough-wren.sprite';
import {
  WrenWayfindingService,
  WrenWayfindTarget,
} from './wren-wayfinding.service';

const FRAMES = 6;
const BOX_W = 84; // px, on-screen wren frame width
const BOX_H = 75; // px, on-screen wren frame height (registered cell aspect 266:237)
const STRIP_W = BOX_W * FRAMES; // background-size width (504px)
const FLIGHT_MS = 640;
const DODGE_MS = 420; // quicker than a step flight — it's just getting out of the way
const FADE_MS = 220;
const OFFSCREEN = { x: -9999, y: -9999 }; // wren parked / no active tour
const DOCK_MARGIN = 24; // px gap from the viewport's bottom-right corner when docked
const PERCH_OVERLAP = 2; // px the perched wren's feet tuck onto the title's top edge (sits just above the text)

// Mouse-dodge: the host is pointer-events:none (so the wren never blocks clicks),
// which also means the wren element never receives hover. Detect proximity with a
// document-level mousemove against the wren's tracked position instead.
const DODGE_PROXIMITY = 72; // px from wren center that triggers a dodge
const DODGE_DISTANCE = 110; // px the wren hops aside to clear the spot
const DODGE_COOLDOWN_MS = 500;

// Click-driven wayfinding: the docked wren flies to an element and HOLDS,
// pointing at it with a prompt ("Click Labor") until the user clicks that
// control — never on a timer (Tim: "it landed but flew away before I clicked").
// A click on the anchor advances to the next hop (or redocks if it was the last);
// a click anywhere else dismisses. No walkthrough record involved.

/**
 * A small wren that serves two non-tour roles (driver.js owns the walkthrough
 * spotlight/popover now, so the wren no longer flies step-to-step during a tour):
 *  - DOCKED launcher: parks in the viewport's bottom-right corner (or perches on
 *    the open Ask Ryn panel). It is clickable and does NOT flee the cursor —
 *    clicking it opens the Ask Ryn chat.
 *  - WAYFINDING: on an Ask Ryn `fly` action it flies to a target element and
 *    holds, pointing at it with a prompt until the user clicks (arcing, flapping,
 *    flipping to face travel).
 *
 * Pure presentation otherwise — it owns its own animation and reduced-motion
 * fallback and edits none of the player's state. Clicking the docked launcher
 * opens the chat via the shared AskRynUiService (providedIn root), the same handle
 * any other Ask Ryn trigger uses; an Ask Ryn `walkthrough` action dispatches
 * `loadWalkthrough` so the driver.js player takes over from there.
 */
@Component({
  selector: 'ups-walkthrough-wren',
  templateUrl: './walkthrough-wren.component.html',
  styleUrls: ['./walkthrough-wren.component.scss'],
  standalone: false, // declared in InAppHelpModule (Angular 21 defaults @Component to standalone:true)
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalkthroughWrenComponent {
  readonly spriteUrl = `url('${WREN_FLIGHT_SPRITE}')`;
  readonly stripWidth = STRIP_W;

  /** True while parked as the bottom-right launcher (no active walkthrough). A
   * signal so the template's clickable/a11y bindings react under OnPush. */
  protected readonly docked = signal(false);

  /** Speech bubble shown while the wren holds on a wayfind target, or null. The
   * bubble is anchored to the (stationary) target rect, pointer-events:none so it
   * never blocks the click on the control beneath. */
  protected readonly wayfindPrompt = signal<{
    text: string;
    x: number;
    y: number;
    below: boolean;
  } | null>(null);

  private readonly askRynUi = inject(AskRynUiService);
  private readonly wayfindingSvc = inject(WrenWayfindingService);
  private readonly store = inject(Store);

  private readonly wrenRef = viewChild<ElementRef<HTMLElement>>('wren');
  private readonly spriteRef = viewChild<ElementRef<HTMLElement>>('sprite');

  private current?: Animation;
  private faceFlip?: number; // pending mid-flight facing flip (perch climb → descent)
  private perchFollowRaf?: number; // rAF handle: tracks the panel while perched
  private pos = { ...OFFSCREEN }; // wren's current viewport center
  private dodging = false;
  private lastDodgeAt = 0;
  private wayfinding = false; // mid ephemeral fly-to-element (re-entrancy guard)
  private wayfindEl?: HTMLElement; // the control the wren is currently pointing at
  private wayfindClick?: (e: MouseEvent) => void; // document-capture click observer

  private readonly reduceMotion =
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  /** Which mode was last applied, so dock() runs once on entry (not per effect
   * tick). A plain field — NOT read through a signal — so it can't feed back into
   * the effect's dependency graph. */
  private currentMode: 'dock' | 'hidden' | null = null;

  /** Last-applied chat open state, so applyChatPerch only re-homes on an actual
   * open↔close transition (not on unrelated effect ticks). */
  private wasChatOpen = false;

  constructor() {
    // Re-apply the dock/hidden mode whenever the wren viewChild resolves or the
    // Ask Ryn availability changes (both read in applyMode, so the effect tracks
    // them).
    effect(() => this.applyMode());

    // Ephemeral wayfinding: react when something sets a fly-to target (e.g. the
    // Ask Ryn 'fly' action).
    effect(() => this.applyWayfind());

    // Ask Ryn actions: when a chat reply carries an action, act on it. 'fly' →
    // ephemeral wayfind to the element; 'walkthrough' → start that tour. Then
    // consume so the 3s chat poll can't re-fire it.
    effect(() => this.applyAskRynAction());

    // Chat open/close: the docked launcher flies UP to perch on top of the Ask
    // Ryn panel when the chat opens (ready to launch a wayfind flight from
    // there), and returns to the bottom-right corner when it closes.
    effect(() => this.applyChatPerch());

    // The constructor effect can first run BEFORE the viewChild query resolves;
    // this guarantees one application after the element exists, so the launcher
    // reliably enters its docked (pointer-events:auto, clickable) state instead
    // of staying pointer-events:none and letting clicks fall through the host.
    afterNextRender(() => this.applyMode());

    if (typeof window !== 'undefined') {
      window.addEventListener('mousemove', this.onMouseMove, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      inject(DestroyRef).onDestroy(() => {
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('resize', this.onResize);
        this.disarmWayfindClick();
        this.stopPerchFollow();
      });
    }
  }

  /** Dock the wren as the Ask Ryn launcher once the element exists. dock() runs
   * only on entry into docked mode (not per effect tick). Wayfinding flights are
   * driven separately by applyWayfind(). */
  private applyMode(): void {
    const wren = this.wrenRef()?.nativeElement;
    if (!wren) {
      return; // view not ready yet — afterNextRender / the next tick will retry
    }
    // Dock as the Ask Ryn launcher only when Ask Ryn is available to this user;
    // otherwise stay parked offscreen. Wayfinding is gated separately in
    // applyWayfind(), so a gated-out user still gets 'fly to element' guidance —
    // just no idle launcher.
    if (!this.askRynUi.canUse()) {
      if (this.currentMode !== 'hidden') {
        this.currentMode = 'hidden';
        this.park(wren);
      }
      return;
    }
    if (this.currentMode !== 'dock') {
      this.currentMode = 'dock';
      this.dock(wren);
    }
  }

  /** React to an Ask Ryn action on the latest reply. 'fly' → wayfind to the
   * element; 'walkthrough' → load that tour to its intro. consumeAction() clears
   * it (one extra effect tick that returns early — no loop). */
  private applyAskRynAction(): void {
    const action = this.askRynUi.pendingAction();
    if (!action) {
      return;
    }
    if (action.kind === 'fly' && action.helpPath) {
      this.wayfindingSvc.flyTo(action.helpPath);
    } else if (action.kind === 'walkthrough' && action.walkthroughId != null) {
      // Land on the tour's intro ("what this covers" + Begin), NOT step 0. A
      // chat-launched tour shouldn't yank the user straight into the spotlight —
      // the intro is the consent front door. (The intro state owns the view until
      // Begin anyway, so jumping to step 0 here is a no-op; we omit it on purpose.)
      this.store.dispatch(
        HelpActions.loadWalkthrough({ documentId: action.walkthroughId })
      );
    } else if (action.kind === 'playTour' && action.document) {
      // On-request AI tour: the document was authored inline by a Ryn from this
      // page's anchors, so there is no id to fetch. playWalkthrough plays it
      // in-memory through the same driver.js player (no DB row, same intro
      // front door as loadWalkthrough).
      this.store.dispatch(
        HelpActions.playWalkthrough({ document: action.document })
      );
    }
    this.askRynUi.consumeAction();
  }

  /** Re-home the resting launcher when the Ask Ryn chat opens or closes: glide
   * up to perch on the panel's top edge on open, back to the bottom-right corner
   * on close. dockTarget() resolves the right spot (it perches when the panel is
   * mounted), so this just re-runs dock() at the transition. An active
   * flight/wayfind owns the position and re-homes itself on finish, so skip then. */
  private applyChatPerch(): void {
    const open = this.askRynUi.isOpen();
    if (open === this.wasChatOpen) {
      return;
    }
    this.wasChatOpen = open;
    const wren = this.wrenRef()?.nativeElement;
    if (!wren || this.wayfinding) {
      return;
    }
    // Re-home on the NEXT frame so the panel has mounted (open) or unmounted
    // (close) in the DOM before dockTarget() measures it. On close especially:
    // measuring synchronously can still see the panel rect and re-perch instead
    // of flying back to the corner. After a frame the @if has settled, so open →
    // perch on the panel, close → glide back to the bottom-right start point.
    if (typeof requestAnimationFrame === 'undefined') {
      this.dock(wren);
      return;
    }
    requestAnimationFrame(() => {
      if (this.wayfinding) {
        return; // an active wayfind took over; it re-homes on finish
      }
      if (this.askRynUi.isOpen() === open) {
        this.dock(wren);
      }
    });
  }

  /** React to a wayfinding target: fly the docked wren to the element, point at
   * it, then return to the dock. Concurrent wayfinds are dropped (guarded) until
   * the current one redocks. */
  private applyWayfind(): void {
    const target = this.wayfindingSvc.target();
    const wren = this.wrenRef()?.nativeElement;
    if (!wren || !target || this.wayfinding) {
      return;
    }
    this.wayfindTo(wren, target);
  }

  /** Fly to the target element's center and HOLD, pointing at it with a prompt
   * until the user clicks. Click-driven, never timed (Tim: "it landed but flew
   * away before I clicked"). The on-settle callback shows the prompt and arms the
   * click observer; the click decides whether to advance or redock. */
  private wayfindTo(wren: HTMLElement, target: WrenWayfindTarget): void {
    this.stopPerchFollow(); // wayfind perches on a control, not the panel
    this.wayfinding = true;
    this.wayfindEl = target.el;
    // FLIGHT-style: not the launcher. pointer-events:none so the user's click
    // lands on the real control beneath the perched wren, not on the bird. The
    // dodge is suppressed separately (onMouseMove) so the wren still holds still.
    this.docked.set(false);
    const to = this.center(target.rect);

    this.flyArc(wren, { ...this.pos }, to, FLIGHT_MS, () => {
      this.showWayfindPrompt(target);
      this.armWayfindClick();
    });
  }

  /** Position the prompt bubble over the (stationary) target. Flips below the
   * control when there isn't room above near the top of the viewport. */
  private showWayfindPrompt(target: WrenWayfindTarget): void {
    const r = target.rect;
    const text = target.text?.trim() || this.derivePrompt(target.el);
    const below = r.top < 96; // not enough room above → flip under the control
    // Clamp the bubble's center so the pill stays on screen for anchors hugging
    // the left edge (left-nav controls at x≈0) — caught in live wayfind verify.
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const halfBubble = 116; // ~max-width/2 + padding; keep the pill on screen
    const cx = r.left + r.width / 2;
    this.wayfindPrompt.set({
      text,
      x: Math.min(Math.max(cx, halfBubble + 8), vw - halfBubble - 8),
      y: below ? r.bottom : r.top,
      below,
    });
  }

  private clearWayfindPrompt(): void {
    this.wayfindPrompt.set(null);
  }

  /** A readable default prompt derived from the target when the reply carried no
   * copy of its own (today's single-helpPath 'fly' action). Once the action
   * contract carries per-hop text, that wins (see showWayfindPrompt). */
  private derivePrompt(el: HTMLElement): string {
    const raw = (el.getAttribute('aria-label') || el.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!raw) {
      return 'Click here to continue';
    }
    const label = raw.length > 28 ? `${raw.slice(0, 27)}…` : raw;
    return `Click ${label}`;
  }

  /** Observe document clicks while holding on a target. Capture phase + passive:
   * we never swallow the click — the real control must still navigate/open. */
  private armWayfindClick(): void {
    if (this.wayfindClick || typeof document === 'undefined') {
      return;
    }
    this.wayfindClick = (e: MouseEvent) => this.onWayfindClick(e);
    document.addEventListener('click', this.wayfindClick, {
      capture: true,
      passive: true,
    });
  }

  private disarmWayfindClick(): void {
    if (this.wayfindClick && typeof document !== 'undefined') {
      document.removeEventListener('click', this.wayfindClick, {
        capture: true,
      });
    }
    this.wayfindClick = undefined;
  }

  /** A click while pointing: on the anchor → advance to the next hop (or finish);
   * anywhere else → dismiss. Either way the held wren stops holding. */
  private onWayfindClick(e: MouseEvent): void {
    const wren = this.wrenRef()?.nativeElement;
    if (!wren) {
      return;
    }
    const onAnchor =
      !!this.wayfindEl &&
      e.target instanceof Node &&
      this.wayfindEl.contains(e.target);
    if (onAnchor) {
      const next = this.wayfindingSvc.advance();
      if (next) {
        this.disarmWayfindClick();
        this.clearWayfindPrompt();
        this.wayfindEl = next.el;
        this.wayfindTo(wren, next); // fly the next hop; re-arms on settle
        return;
      }
      if (this.wayfindingSvc.pendingResolve()) {
        // Cross-route: the click navigated and the next hop hasn't rendered yet.
        // Release the hold WITHOUT clearing the route — the service is polling
        // and re-fires `target` when the hop appears, at which point applyWayfind
        // flies the (now docked) wren to it. On timeout the service ends the
        // route itself; the wren is already home, so there's nothing to undo.
        this.disarmWayfindClick();
        this.clearWayfindPrompt();
        this.wayfinding = false;
        this.wayfindEl = undefined;
        this.dock(wren);
        return;
      }
    }
    this.endWayfind(wren); // last hop done, or clicked away → redock
  }

  /** Tear down wayfind state WITHOUT docking (a starting tour is taking over). */
  private cancelWayfind(): void {
    this.disarmWayfindClick();
    this.clearWayfindPrompt();
    this.wayfinding = false;
    this.wayfindEl = undefined;
    this.wayfindingSvc.clear();
  }

  /** End a wayfind and return the wren to its dock. */
  private endWayfind(wren: HTMLElement): void {
    this.cancelWayfind();
    this.dock(wren);
  }

  private center(r: DOMRect): { x: number; y: number } {
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  private settleAt(wren: HTMLElement, p: { x: number; y: number }): void {
    wren.style.offsetPath = 'none';
    wren.style.transform = `translate(${p.x - BOX_W / 2}px, ${p.y - BOX_H / 2}px)`;
    this.pos = { x: p.x, y: p.y };
  }

  /** Where the resting wren lives: perched on the open Ask Ryn panel if it's
   * mounted, otherwise the viewport's bottom-right corner. */
  private dockTarget(): { x: number; y: number } {
    return this.panelPerchTarget() ?? this.cornerTarget();
  }

  /** Viewport-space center of the bottom-right corner dock slot. */
  private cornerTarget(): { x: number; y: number } {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    return {
      x: vw - DOCK_MARGIN - BOX_W / 2,
      y: vh - DOCK_MARGIN - BOX_H / 2,
    };
  }

  /** Cancel a pending mid-flight facing flip (a new flight supersedes it). */
  private clearFaceFlip(): void {
    if (this.faceFlip !== undefined) {
      window.clearTimeout(this.faceFlip);
      this.faceFlip = undefined;
    }
  }

  /** Perch slot directly above the open panel's "Ask Ryn" title text: centered
   * over the title with the wren's feet tucked onto its top edge (body above
   * it). Returns null when the panel isn't mounted (chat closed) so the wren
   * falls back to the corner dock; falls back to the panel header's top edge if
   * the title element can't be found. The `.ask-ryn-panel` / `.ask-ryn-panel__title`
   * elements are rendered by the Ask Ryn host — a stable contract, since this
   * component already depends on that lib (AskRynUiService). */
  private panelPerchTarget(): { x: number; y: number } | null {
    if (typeof document === 'undefined') {
      return null;
    }
    const panel = document.querySelector('.ask-ryn-panel');
    if (!panel) {
      return null;
    }
    const anchor = panel.querySelector('.ask-ryn-panel__title') ?? panel;
    const r = anchor.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) {
      return null; // not laid out yet
    }
    return { x: r.left + r.width / 2, y: r.top - BOX_H / 2 + PERCH_OVERLAP };
  }

  /** While perched on the open Ask Ryn panel, track it every frame so the wren
   * stays glued through a drag (header) or resize (top-left handle) — both move
   * the panel via top/left, which moves its title rect. A per-frame follow (vs.
   * piggybacking on mousemove) converges to the panel's resting spot within one
   * frame, so the wren never ends a drag visibly offset. Skips while an
   * animation is running (the glide-to-perch lands first), and self-stops the
   * moment the wren leaves the panel perch (flight, wayfind, close, or corner). */
  private startPerchFollow(): void {
    if (
      this.perchFollowRaf !== undefined ||
      typeof requestAnimationFrame === 'undefined'
    ) {
      return; // already following, or no rAF (SSR)
    }
    const step = (): void => {
      if (
        this.currentMode !== 'dock' ||
        !this.docked() ||
        !this.askRynUi.isOpen()
      ) {
        this.perchFollowRaf = undefined; // left the perch → stop the loop
        return;
      }
      const wren = this.wrenRef()?.nativeElement;
      if (wren && this.current?.playState !== 'running') {
        const perch = this.panelPerchTarget();
        if (perch && (perch.x !== this.pos.x || perch.y !== this.pos.y)) {
          this.settleAt(wren, perch);
        }
      }
      this.perchFollowRaf = requestAnimationFrame(step);
    };
    this.perchFollowRaf = requestAnimationFrame(step);
  }

  private stopPerchFollow(): void {
    if (
      this.perchFollowRaf !== undefined &&
      typeof cancelAnimationFrame !== 'undefined'
    ) {
      cancelAnimationFrame(this.perchFollowRaf);
    }
    this.perchFollowRaf = undefined;
  }

  /** Park the wren bottom-right as the clickable launcher. Glides there from its
   * current spot if it's already on screen (e.g. a tour just ended); otherwise
   * fades in at the corner. The clickable/no-flee behavior is driven by `docked`. */
  private dock(wren: HTMLElement): void {
    this.docked.set(true);
    const perch = this.panelPerchTarget();
    const target = perch ?? this.cornerTarget();
    const isPerch = !!perch;
    const sprite = this.spriteRef()?.nativeElement;
    this.clearFaceFlip();
    // Keep glued to the panel while perched; release at the corner dock.
    if (isPerch) {
      this.startPerchFollow();
    } else {
      this.stopPerchFollow();
    }

    if (this.pos.x >= 0 && !this.reduceMotion?.matches) {
      // Glide there from wherever it is. Resting facing:
      //  - CORNER: face INWARD toward the page (left = scaleX(-1)); watches the page.
      //  - PERCH: face the CHAT (right) so she lands looking into the panel to read.
      // For a perch climb she leaves facing left (up-and-left travel), then flips
      // to face right at the arc's apex so the descent + landing read as "turning
      // to look at the chat."
      this.flyArc(wren, { ...this.pos }, target, FLIGHT_MS, () =>
        sprite?.classList.toggle('face-left', !isPerch)
      );
      if (isPerch && sprite) {
        this.faceFlip = window.setTimeout(
          () => sprite.classList.remove('face-left'), // turn to face the chat on descent
          FLIGHT_MS * 0.5 // ~arc apex: ascent (left) → descent (right)
        );
      }
      return;
    }
    this.current?.cancel();
    sprite?.classList.remove('flying');
    sprite?.classList.toggle('face-left', !isPerch); // perch → face chat; corner → face page
    wren.style.opacity = '0';
    this.settleAt(wren, target);
    this.current = wren.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: FADE_MS,
      easing: 'ease-out',
      fill: 'forwards',
    });
  }

  /** Park the wren fully offscreen + non-interactive: Ask Ryn is gated off for
   * this user. docked(false) drops pointer-events and focusability; the offscreen
   * pos makes onMouseMove ignore it. A wayfind still overrides this via
   * applyWayfind (its gate is Ask Ryn availability-independent). */
  private park(wren: HTMLElement): void {
    this.stopPerchFollow();
    this.clearFaceFlip();
    this.current?.cancel();
    this.docked.set(false);
    this.spriteRef()?.nativeElement.classList.remove('flying');
    wren.style.opacity = '0';
    this.settleAt(wren, OFFSCREEN);
  }

  /**
   * Control point for a quadratic arc that always bows PERPENDICULAR to the
   * flight line: horizontal-ish flights bow upward (birds lift); near-vertical
   * flights bow sideways toward the roomier edge, tracing a semicircle instead
   * of a straight vertical line.
   */
  private arcControl(
    from: { x: number; y: number },
    to: { x: number; y: number }
  ): { x: number; y: number } {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.hypot(dx, dy) || 1;
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;

    // Near-vertical: bow sideways = semicircle (the straight-line case Tim flagged).
    if (Math.abs(dx) < Math.abs(dy) * 0.4) {
      const dir = midX < vw / 2 ? 1 : -1; // toward the side with more room
      const sideBulge = Math.min(len * 0.6, 220);
      return { x: midX + dir * sideBulge, y: midY };
    }

    // Otherwise bow upward, perpendicular to the chord.
    let px = -dy / len;
    let py = dx / len;
    if (py > 0) {
      px = -px;
      py = -py; // ensure the bow lifts (negative y is up)
    }
    const bulge = Math.min(170, Math.abs(dx) * 0.35 + 70);
    return { x: midX + px * bulge, y: midY + py * bulge };
  }

  /** Shared flight: perpendicular-bow arc, flap, direction-flip, trail, settle. */
  private flyArc(
    wren: HTMLElement,
    from: { x: number; y: number },
    to: { x: number; y: number },
    durationMs: number,
    done?: () => void,
    straight = false
  ): void {
    this.current?.cancel();
    this.clearFaceFlip(); // a new flight supersedes any pending perch facing flip
    const sprite = this.spriteRef()?.nativeElement;

    if (this.reduceMotion?.matches) {
      sprite?.classList.remove('flying');
      this.settleAt(wren, to);
      wren.style.opacity = '1';
      done?.();
      return;
    }

    sprite?.classList.toggle('face-left', to.x < from.x); // face travel direction
    sprite?.classList.add('flying');

    // A "shoo"/dodge flies STRAIGHT (control point on the chord midpoint traces a
    // straight line); walkthrough-step flights keep the natural perpendicular bow.
    const ctrl = straight
      ? { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 }
      : this.arcControl(from, to);
    const path = `path("M ${from.x - BOX_W / 2} ${from.y - BOX_H / 2} Q ${ctrl.x - BOX_W / 2} ${ctrl.y - BOX_H / 2} ${to.x - BOX_W / 2} ${to.y - BOX_H / 2}")`;

    wren.style.transform = 'none';
    wren.style.offsetPath = path;
    // Ryn never tilts: she only faces left or right via the sprite's scaleX flip.
    // Pin offset-rotate to 0 so riding the motion path translates the body without
    // rotating it to the path tangent (the default `auto` angles the body).
    wren.style.offsetRotate = '0deg';
    wren.style.opacity = '1';

    this.current = wren.animate(
      [{ offsetDistance: '0%' }, { offsetDistance: '100%' }],
      {
        duration: durationMs,
        easing: 'cubic-bezier(.45,.05,.3,1)',
        fill: 'forwards',
      }
    );
    this.spawnTrail(wren, from, ctrl, to, durationMs);
    this.current.onfinish = () => {
      sprite?.classList.remove('flying');
      this.settleAt(wren, to);
      done?.();
    };
  }

  /** Activate the docked launcher (click / Enter / Space) → open Ask Ryn. No-op
   * during a walkthrough so a flight tap doesn't fire it. */
  protected onWrenActivate(): void {
    if (this.docked()) {
      this.askRynUi.open();
    }
  }

  /** Keep the launcher pinned to the corner when the viewport resizes. */
  private readonly onResize = (): void => {
    const wren = this.wrenRef()?.nativeElement;
    if (wren && this.docked()) {
      this.settleAt(wren, this.dockTarget());
    }
  };

  /** Flee ~110px AWAY from the cursor's approach side; wrap to the opposite page
   * edge if that would run off-screen, so the wren never ping-pongs between two
   * spots when the cleared one is also blocked. */
  private readonly onMouseMove = (e: MouseEvent): void => {
    const wren = this.wrenRef()?.nativeElement;
    if (!wren || this.pos.x < 0) {
      return; // no wren, or parked offscreen
    }

    // DOCKED launcher OR a wayfind hold: never move (the launcher must stay
    // clickable; the wayfind wren must stay perched on its control regardless of
    // docked state — Tim's "suppress the dodge during wayfind"). Flap in place
    // when the cursor comes near as a liveliness/"click me" cue. Only while
    // resting; a running glide/fly/fade owns the sprite's flap itself.
    if (this.docked() || this.wayfinding) {
      if (this.current?.playState !== 'running') {
        const ndx = e.clientX - this.pos.x;
        const ndy = e.clientY - this.pos.y;
        const near = ndx * ndx + ndy * ndy <= DODGE_PROXIMITY * DODGE_PROXIMITY;
        this.spriteRef()?.nativeElement.classList.toggle('flying', near);
      }
      return;
    }

    if (this.dodging) {
      return; // mid-dodge
    }
    const now = performance.now();
    if (now - this.lastDodgeAt < DODGE_COOLDOWN_MS) {
      return;
    }
    const dx = e.clientX - this.pos.x;
    const dy = e.clientY - this.pos.y;
    if (dx * dx + dy * dy > DODGE_PROXIMITY * DODGE_PROXIMITY) {
      return;
    }

    const vw = window.innerWidth;
    const minX = BOX_W / 2 + 8;
    const maxX = vw - BOX_W / 2 - 8;

    // Directional: hop away from the side the cursor came from (cursor on the
    // right → flee left, and vice versa). movementX breaks the dead-on tie.
    const approachFromRight = dx > 0 || (dx === 0 && e.movementX >= 0);
    const dir = approachFromRight ? -1 : 1;

    // Wrap: a hop that would run off one edge reappears on the opposite edge.
    let targetX = this.pos.x + dir * DODGE_DISTANCE;
    if (targetX < minX) {
      targetX = maxX;
    } else if (targetX > maxX) {
      targetX = minX;
    }

    this.dodging = true;
    this.lastDodgeAt = now;

    if (this.reduceMotion?.matches) {
      this.settleAt(wren, { x: targetX, y: this.pos.y });
      this.dodging = false;
      return;
    }
    this.flyArc(
      wren,
      this.pos,
      { x: targetX, y: this.pos.y },
      DODGE_MS,
      () => {
        this.dodging = false;
      },
      true // shoo flies straight left/right, no arc bow
    );
  };

  /** Fading dots sampled along the flight path so a glance reads as motion. */
  private spawnTrail(
    wren: HTMLElement,
    from: { x: number; y: number },
    ctrl: { x: number; y: number },
    to: { x: number; y: number },
    durationMs: number
  ): void {
    const host = wren.parentElement;
    if (!host) {
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const bx =
        (1 - t) * (1 - t) * from.x + 2 * (1 - t) * t * ctrl.x + t * t * to.x;
      const by =
        (1 - t) * (1 - t) * from.y + 2 * (1 - t) * t * ctrl.y + t * t * to.y;
      // Inline-styled: the dot is appended to the host (outside this
      // component's view encapsulation), so scoped CSS would not reach it.
      const dot = document.createElement('span');
      dot.style.cssText =
        'position:fixed;width:7px;height:7px;margin:-3.5px 0 0 -3.5px;' +
        'border-radius:50%;background:var(--color-primary,#2f6fed);' +
        'pointer-events:none;z-index:2147483645;';
      dot.style.left = `${bx}px`;
      dot.style.top = `${by}px`;
      host.appendChild(dot);
      dot.animate(
        [
          { opacity: 0.45, transform: 'scale(1)' },
          { opacity: 0, transform: 'scale(0.4)' },
        ],
        { duration: 700, easing: 'ease-out', fill: 'forwards' }
      );
      window.setTimeout(() => dot.remove(), 720);
      if (t < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }
}
